import React from "react";
import { IProjectV0Ex } from "../../../api/Admin/dbProjectsApi";
import { makeStyles } from "@mui/styles";
import { Link, Grid, Chip, Avatar } from "@mui/material";
// eslint-disable-next-line import/named
import { Theme } from "@mui/material/styles";
import { format } from "date-fns";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridToolbar,
} from "@mui/x-data-grid";
import { useDataGridStyles } from "../../../hooks/useDataGridStyles";
import DoneIcon from "@mui/icons-material/Done";
import { useNavigate } from "react-router-dom";
import * as urls from "../../../types/Constants";

//https://devexpress.github.io/devextreme-reactive/react/grid/demos/featured/tree-data/

export interface IOrdersGridProps {
  projects: IProjectV0Ex[];
}

const ProjectsGrid: React.FC<IOrdersGridProps> = ({ projects }) => {
  const navigate = useNavigate();

  // Safety check for projects data to prevent Object.keys errors
  const safeProjects = projects || [];
  if (!Array.isArray(safeProjects)) {
    console.warn("ProjectsGrid: projects is not an array", projects);
    return <div>Error: Invalid projects data</div>;
  }

  const { styles } = useDataGridStyles({
    variant: "common",
    type: "purchases",
  });

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Program",
      width: 480,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row;
        if (!row || !row.programId || !row.name) {
          return <span>-</span>;
        }

        const url = urls.getProjectUrl(row.programId);
        return (
          <Link
            href="#"
            color={row.isCancelled === true ? "secondary" : "primary"}
            onClick={() => navigate(url)}
            variant="inherit"
          >
            {row.name}
          </Link>
        );
      },
    },
    { field: "orderId", headerName: "P.O.", width: 120 },
    {
      field: "orderDate",
      headerName: "Date",
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row;
        if (!row || !row.orderDate) {
          return <span>-</span>;
        }
        return format(new Date(row.orderDate), "dd/MM/yyyy");
      },
    },
    {
      field: "pending",
      headerName: "Pending",
      width: 180,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row;
        // Safety check for row and pending data
        if (!row || !Array.isArray(row.pending)) {
          return <div>-</div>;
        }

        return (
          <div>
            {row.pending.length === 0 ? (
              <div style={{ display: "flex", justifyContent: "center" }}>
                {row.isCancelled ? (
                  <Chip
                    label="Canceled"
                    color="secondary"
                    onDelete={(e) => e.preventDefault()}
                    deleteIcon={<DoneIcon />}
                  />
                ) : (
                  <Chip
                    label="Complete"
                    color="primary"
                    onDelete={(e) => e.preventDefault()}
                    deleteIcon={<DoneIcon />}
                  />
                )}
              </div>
            ) : (
              <>
                <Grid
                  container
                  spacing={1}
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                >
                  {row.pending.map((x) => (
                    <Grid key={x}>
                      <Avatar className={gridStyles.avatarSmall}>
                        {x.substring(0, 1)}
                      </Avatar>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </div>
        );
      },
    },
    { field: "userName", headerName: "End user", width: 240 },
    { field: "userCompanyName", headerName: "Company", width: 240 },
  ];

  const useGridStyles = makeStyles((theme: Theme) => ({
    link: {
      padding: "2px 4px 2px 10px",
    },
    avatarSmall: {
      width: theme.spacing(4),
      height: theme.spacing(4),
    },
  }));
  const gridStyles = useGridStyles();

  return (
    <div style={{ height: "80%", width: "100%" }}>
      <DataGrid
        rows={safeProjects}
        columns={columns}
        getRowId={(row) => row.programId || row.orderId || Math.random()}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 100 },
          },
          sorting: {
            sortModel: [{ field: "orderDate", sort: "desc" }],
          },
          columns: {
            columnVisibilityModel: {
              userId: false,
            },
          },
        }}
        slots={{ toolbar: GridToolbar }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 500 },
          },
        }}
        sx={styles}
        pageSizeOptions={[10, 25, 50, 100]}
        checkboxSelection={false}
        disableRowSelectionOnClick
      />
    </div>
  );
};

export default ProjectsGrid;
